import {Decorator, FormApi, getIn} from 'final-form';
import noop from 'lodash/noop';

import {MODALS_ID} from '../Modals';

type TOptions = {
  id: string;
  offsetTop?: number;
};

const MARGIN_TOP_AFTER_SCROLL = 8;

export const isFocusableInput = (element: HTMLElement) =>
  element && typeof element.focus === 'function';

export const getFields = (id: TOptions['id']): HTMLElement[] => {
  const form = window.document.getElementById(id);

  if (!form) {
    return [];
  }

  const formFields = [...form.querySelectorAll('[data-field]')] as HTMLElement[];
  // TODO: leave it here until http://gitlab.mfms/chat-center/ui/workplace ts strict mode
  const result: HTMLElement[] = [];

  for (let i = 0; i < formFields.length; i += 1) {
    if (isFocusableInput(formFields[i] as HTMLElement)) {
      result.push(formFields[i]);
    }
  }

  return result;
};

export const findField = (
  fields: HTMLElement[],
  errors: Record<string, string>,
): HTMLElement | void =>
  fields.find((field) => {
    const name = (field as HTMLInputElement).name || field.getAttribute('name');

    return name && getIn(errors, name);
  });

const createDecorator =
  <FormValues = TAnyObject>({id, offsetTop = 0}: TOptions): Decorator<FormValues, FormValues> =>
  (form: FormApi<FormValues, FormValues>) => {
    const focusOnFirstError = (errors: Record<string, string>) => {
      const firstField = findField(getFields(id), errors);

      if (firstField) {
        if (document.getElementById(MODALS_ID)?.hasChildNodes()) {
          firstField.scrollIntoView({
            behavior: 'smooth',
          });
        } else {
          const top =
            firstField.getBoundingClientRect().top +
            window.scrollY -
            (offsetTop + MARGIN_TOP_AFTER_SCROLL);

          window.scroll({
            top,
            behavior: 'smooth',
          });
        }

        const input = firstField.querySelector('input');
        // const focusableElement = firstField.querySelector('[tabindex]') as HTMLInputElement;

        if (input) {
          input.focus();
        }
        // else if (focusableElement) {
        //   focusableElement.focus();
        // } else {
        //   firstField.focus();
        // }
      }
    };

    // Save original submit function
    const originalSubmit = form.submit;

    // Subscribe to errors, and keep a local copy of them
    let state: {
      errors?: Record<string, string>;
      submitErrors?: Record<string, string>;
    } = {};

    const unsubscribe = form.subscribe(
      (nextState) => {
        state = nextState;
      },
      {errors: true, submitErrors: true},
    );

    // What to do after submit
    const afterSubmit = () => {
      const {errors, submitErrors} = state;

      if (errors && Object.keys(errors).length) {
        focusOnFirstError(errors);
      } else if (submitErrors && Object.keys(submitErrors).length) {
        focusOnFirstError(submitErrors);
      }
    };

    // Rewrite submit function
    // eslint-disable-next-line no-param-reassign
    form.submit = () => {
      const result = originalSubmit.call(form);

      if (result && typeof result.then === 'function') {
        // async
        result.then(afterSubmit, noop);
      } else {
        // sync
        afterSubmit();
      }

      return result;
    };

    return () => {
      unsubscribe();
      // eslint-disable-next-line no-param-reassign
      form.submit = originalSubmit;
    };
  };

export default createDecorator;
